import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ProfileService } from '../../services/profile/profile.service';
import { AlertBannerService, AlertType } from '../../lib/alert-banner';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

const RATE_LIMIT_STORAGE_KEY = `mobile_otp_rate_limit_${environment.env}`;
const RATE_LIMIT_DURATION_MS = 600000; // 10 minutes

type RateLimitType = 'send' | 'verify' | null;

interface RateLimitState {
  type: RateLimitType;
  endTime: number;
}

@Component({
  selector: 'app-mobile-verification-modal',
  templateUrl: './mobile-verification-modal.component.pug',
  styleUrls: ['./mobile-verification-modal.component.scss'],
  standalone: false
})
export class MobileVerificationModalComponent implements OnInit, OnDestroy {
  @Input() countryCode: string = '';
  @Input() phoneNumber: string = '';
  @Input() lastResendTimestamp: number | null = null;
  /** When true, rate limit is cleared on init so modal opens in fresh state (e.g. after successful send). */
  @Input() clearRateLimitOnOpen: boolean = false;
  /** When set, modal opens with this rate limit applied (e.g. when Save returned MOBILE_OTP_*_LIMIT_REACHED). */
  @Input() initialRateLimitType: RateLimitType = null;
  /** When true (ent-10313), pass draft phone on verify because it was not persisted on send. */
  @Input() deferMobilePersist: boolean = false;

  public event: EventEmitter<any> = new EventEmitter();
  @Output() resendEvent: EventEmitter<number> = new EventEmitter();

  // OTP configuration
  readonly OTP_LENGTH = 6;
  private readonly RESEND_COOLDOWN_MS = 60000; // 60 seconds

  verificationCode: string = '';
  type: string = 'text';
  isLoading: boolean = false;
  errorFeedback: boolean = false;
  errors = [
    { name: 'invalidCode', msg: $localize`The code is incorrect or has expired, please try again.`, enabled: false }
  ];

  // Resend cooldown state (60 seconds)
  resendDisabled: boolean = false;
  codeResent: boolean = false;
  private resendTimeoutId: any;
  private destroy$ = new Subject<void>();

  // Twilio rate limit state (10 minutes, persisted in localStorage)
  rateLimitType: RateLimitType = null;
  private rateLimitIntervalId: any;
  /** Fixed end time (ms) for current limit; used so we never extend or drift past 10 min */
  private rateLimitEndTime: number | null = null;

  get isValidOtp(): boolean {
    return this.verificationCode.length === this.OTP_LENGTH;
  }

  /** Whether send limit is active (resend disabled, verify enabled) */
  get isSendLimitActive(): boolean {
    return this.rateLimitType === 'send';
  }

  /** Whether verify limit is active (resend + verify disabled, input disabled) */
  get isVerifyLimitActive(): boolean {
    return this.rateLimitType === 'verify';
  }

  /** Resend button disabled: normal cooldown OR any rate limit */
  get isResendDisabled(): boolean {
    return this.resendDisabled || this.isSendLimitActive || this.isVerifyLimitActive;
  }

  /** Verify button disabled: invalid OTP OR verify limit */
  get isVerifyDisabled(): boolean {
    return !this.isValidOtp || this.isLoading || this.isVerifyLimitActive;
  }

  /** Status text shown next to resend link: "Code resent." or rate limit message */
  get resendStatusText(): string {
    if (this.isSendLimitActive) {
      return $localize`You have requested too many codes.`;
    }
    if (this.isVerifyLimitActive) {
      return $localize`Too many incorrect codes. Please try again in a few minutes.`
    }
    if (this.codeResent) {
      return $localize`Code resent.`;
    }
    return '';
  }

  /** Label when resend is enabled: "Send again" after first resend, "Resend code" initially */
  get resendEnabledLabel(): string {
    if (!this.isResendDisabled) {
      return this.codeResent ? $localize`Send again` : $localize`Resend code`;
    }
    return '';
  }

  /** Label when resend is disabled: cooldown (one minute) or send limit (few minutes) */
  get resendDisabledLabel(): string {
    if (this.isSendLimitActive) {
      return $localize`Send again (Try again in a few minutes)`;
    }
    if (this.isResendDisabled && !this.isVerifyLimitActive) {
      return $localize`Send again (Try again in one minute)`;
    }
    return '';
  }

  constructor(
    public _bsModalRef: BsModalRef,
    private profileService: ProfileService,
    private alertBannerService: AlertBannerService,
  ) {
  }

  ngOnInit(): void {
    if (this.clearRateLimitOnOpen) {
      this.clearRateLimit();
    }
    this.restoreRateLimitFromStorage();
    if (this.initialRateLimitType && !this.rateLimitType) {
      this.applyRateLimit(this.initialRateLimitType);
    }
    // Check if we're still within cooldown from a previous resend (only if not in rate limit)
    if (!this.rateLimitType && this.lastResendTimestamp) {
      const elapsed = Date.now() - this.lastResendTimestamp;
      const remaining = this.RESEND_COOLDOWN_MS - elapsed;
      if (remaining > 0) {
        this.resendDisabled = true;
        this.codeResent = true;
        this.resendTimeoutId = setTimeout(() => {
          this.resendDisabled = false;
        }, remaining);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.resendTimeoutId) {
      clearTimeout(this.resendTimeoutId);
    }
    if (this.rateLimitIntervalId) {
      clearInterval(this.rateLimitIntervalId);
    }
  }

  private restoreRateLimitFromStorage(): void {
    try {
      const raw = localStorage.getItem(RATE_LIMIT_STORAGE_KEY);
      if (!raw) return;
      const state: RateLimitState = JSON.parse(raw);
      if (!state?.type || !state?.endTime) return;
      const remaining = Math.ceil((state.endTime - Date.now()) / 1000);
      if (remaining <= 0) {
        localStorage.removeItem(RATE_LIMIT_STORAGE_KEY);
        return;
      }
      this.rateLimitType = state.type;
      this.rateLimitEndTime = state.endTime;
      this.resendDisabled = true;
      this.startRateLimitCountdown();
    } catch {
      localStorage.removeItem(RATE_LIMIT_STORAGE_KEY);
    }
  }

  private applyRateLimit(type: RateLimitType): void {
    let endTime: number;
    try {
      const raw = localStorage.getItem(RATE_LIMIT_STORAGE_KEY);
      const existing = raw ? (JSON.parse(raw) as RateLimitState) : null;
      const now = Date.now();
      if (existing?.endTime && existing.endTime > now) {
        endTime = existing.endTime;
      } else {
        endTime = now + RATE_LIMIT_DURATION_MS;
      }
    } catch {
      endTime = Date.now() + RATE_LIMIT_DURATION_MS;
    }
    this.rateLimitType = type;
    this.rateLimitEndTime = endTime;
    this.resendDisabled = true;
    try {
      localStorage.setItem(RATE_LIMIT_STORAGE_KEY, JSON.stringify({ type, endTime }));
    } catch {
      // ignore
    }
    this.startRateLimitCountdown();
  }

  private startRateLimitCountdown(): void {
    if (this.rateLimitIntervalId) {
      clearInterval(this.rateLimitIntervalId);
    }
    this.rateLimitIntervalId = setInterval(() => {
      const now = Date.now();
      const endTime = this.rateLimitEndTime ?? now;
      const remaining = Math.max(0, Math.ceil((endTime - now) / 1000));
      if (remaining <= 0) {
        this.clearRateLimit();
      }
    }, 1000);
  }

  private clearRateLimit(): void {
    if (this.rateLimitIntervalId) {
      clearInterval(this.rateLimitIntervalId);
      this.rateLimitIntervalId = null;
    }
    this.rateLimitType = null;
    this.rateLimitEndTime = null;
    this.resendDisabled = false;
    this.codeResent = false;
    try {
      localStorage.removeItem(RATE_LIMIT_STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  resendCode() {
    if (this.isResendDisabled || this.isLoading) {
      return;
    }

    this.resetError();
    this.verificationCode = '';
    this.isLoading = true;

    this.profileService.triggerMobileVerification(this.countryCode, this.phoneNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.clearRateLimit();
          this.codeResent = true;
          this.resendDisabled = true;
          // Emit timestamp to parent for persistence across modal instances
          const timestamp = Date.now();
          this.resendEvent.emit(timestamp);
          // Re-enable after cooldown
          this.resendTimeoutId = setTimeout(() => {
            this.resendDisabled = false;
          }, this.RESEND_COOLDOWN_MS);
        },
        error: (err) => {
          this.isLoading = false;
          const code = err?.error?.data?.xref_error;
          if (code === 'MOBILE_OTP_SEND_LIMIT_REACHED') {
            this.applyRateLimit('send');
          } else {
            this.alertBannerService.append([{
              type: AlertType.DANGER,
              message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
              keepOnRouteChange: true
            }]);
          }
        }
      });
  }

  verify() {
    if (this.isVerifyDisabled) {
      return;
    }

    this.resetError();
    this.isLoading = true;

    const verify$ = this.deferMobilePersist
      ? this.profileService.verifyMobile(this.verificationCode, this.countryCode, this.phoneNumber)
      : this.profileService.verifyMobile(this.verificationCode);

    verify$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response?.verified) {
            this.clearRateLimit();
            this.event.emit({
              verified: true,
              mobile: response.mobile,
              mobile_verified: response.mobile_verified
            });
            this._bsModalRef.hide();
          } else {
            this.setError();
          }
        },
        error: (err) => {
          this.isLoading = false;
          const xrefError = err?.error?.data?.xref_error;
          if (xrefError === 'MOBILE_OTP_CHECK_LIMIT_REACHED') {
            this.verificationCode = '';
            this.applyRateLimit('verify');
          } else if (xrefError === 'MOBILE_OTP_INCORRECT') {
            this.setError();
          } else {
            this.alertBannerService.append([{
              type: AlertType.DANGER,
              message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
              keepOnRouteChange: true
            }]);
          }
        }
      });
  }

  cancel() {
    this.event.emit({ cancelled: true });
    this._bsModalRef.hide();
  }

  textValueChanged(value: string) {
    // Only allow digits, limit to OTP_LENGTH characters
    const digitsOnly = (value || '').replace(/\D/g, '').slice(0, this.OTP_LENGTH);
    this.verificationCode = digitsOnly;
    // Clear error when user starts typing a new code
    if (this.errorFeedback) {
      this.resetError();
    }
  }

  /**
   * Sets the error state to show the invalid code message.
   */
  setError() {
    this.errorFeedback = true;
    this.errors = this.errors.map(e => ({ ...e, enabled: e.name === 'invalidCode' }));
  }

  /**
   * Resets the error state.
   */
  resetError() {
    this.errorFeedback = false;
    this.errors = this.errors.map(e => ({ ...e, enabled: false }));
  }
}


